import type { TFunction } from 'i18next'

import { COUNTRIES } from '../../data/countries'
import {
  SEMANTIC_MATCH_LEVELS,
  TARGETING_TYPES,
  parseKeyValueTargets,
  semanticMatchLevel,
  parsePiped,
  type ObjectRef,
  type Targeting,
  type TargetingTypeId,
} from '@/services/targeting/targeting'
import type { Chip } from '../ChipRow'

export interface TargetingSummaryLine {
  id: TargetingTypeId
  /** The type's own name, e.g. "Sites". */
  label: string
  /** One chip per value the type targets. */
  chips: Chip[]
  /** True when the selection excludes rather than includes. */
  negated: boolean
  count: number
}

const chip = (label: string, tone?: Chip['tone']): Chip => ({ label, tone })

const refChips = (refs: ObjectRef[] | undefined, tone?: Chip['tone']): Chip[] =>
  (refs ?? []).map((ref) => chip(ref.name || ref.id, tone))

const textChips = (values: (string | undefined)[], tone?: Chip['tone']): Chip[] =>
  values.filter((value): value is string => !!value).map((value) => chip(value, tone))

/**
 * Keyword and category targeting can require a value, offer it as one of
 * several, or rule it out. A required value is marked with a leading `+`, the
 * way search engines mark a mandatory term.
 */
function pipedChips(values: string[] | undefined): Chip[] {
  const parsed = parsePiped(values)
  return [
    ...textChips(parsed.any, 'include'),
    ...parsed.all.map((value) => chip(`+${value}`, 'required')),
    ...textChips(parsed.none, 'exclude'),
  ]
}

function keyValueChips(targets: Targeting['keyValueTargets'], t: TFunction): Chip[] {
  return parseKeyValueTargets(targets)
    .flat()
    .map((row) =>
      chip(
        `${row.key}: ${row.values || t('targeting.keyValue.anyValue')}`,
        row.not || row.inNotBlock ? 'exclude' : 'include'
      )
    )
}

/**
 * Keyword and category rows always keep their colours: the three lists mean
 * different things — green is one of the alternatives, blue must match, red
 * rules out — and the colour is what tells them apart at a glance.
 */
const ALWAYS_COLOURED: TargetingTypeId[] = ['keyword', 'category']

/**
 * Elsewhere a row only earns its colours when it says both yes and no — a list
 * of plain includes reads better as neutral chips than as a wall of green
 * ticks. A required value keeps its blue whatever the row says.
 */
function tone(chips: Chip[]): Chip[] {
  if (chips.some((c) => c.tone === 'exclude')) return chips
  return chips.map((c) => (c.tone === 'include' ? { ...c, tone: 'neutral' as const } : c))
}

/** One line per targeting type in use, in the order the editor lists them. */
export function summarizeTargeting(
  targeting: Targeting | undefined,
  t: TFunction
): TargetingSummaryLine[] {
  if (!targeting) return []

  const chipsFor = (id: TargetingTypeId, negated: boolean): Chip[] => {
    // A negated target rules out everything it names, whatever the type.
    const listed = negated ? ('exclude' as const) : undefined

    switch (id) {
      case 'publisher':
        return refChips(targeting.publisherTarget?.earningsAccounts, listed)
      case 'site':
        return refChips(targeting.siteTarget?.sites, listed)
      case 'siteGroup':
        return refChips(targeting.siteGroupTarget?.siteGroups, listed)
      case 'adUnit':
        return refChips(targeting.adUnitTarget?.adUnits, listed)
      case 'adUnitMatchingLabel':
        return textChips(
          (targeting.adUnitMatchingLabelTargets ?? []).flatMap((row) => row.matchingLabels ?? [])
        )
      case 'siteCountry':
        return textChips(
          (targeting.siteCountryTarget?.countries ?? []).map(
            (code) => COUNTRIES.find((country) => country.value === code)?.label ?? code
          )
        )
      case 'userSegment': {
        const rows = targeting.userSegmentTargets ?? []
        return [
          ...refChips(
            rows.flatMap((row) => row.userSegments ?? []),
            'include'
          ),
          ...refChips(
            rows.flatMap((row) => row.notUserSegments ?? []),
            'exclude'
          ),
        ]
      }
      case 'namedLocation':
        return textChips(
          [
            ...(targeting.namedLocationTarget?.locations ?? []).map((l) => l.name),
            ...(targeting.namedLocationTarget?.municipalities ?? []).map((m) => m.name),
          ],
          listed
        )
      case 'device':
        // A chip per device type, OS and brand. The rows are alternatives and
        // a row's dimensions must all match, but the values name such
        // different things — a type, an OS, a brand — that reading them as one
        // list loses nothing, and a badge each scans better than one chip
        // holding "Mobile + iOS + Apple".
        return textChips([
          ...new Set(
            (targeting.deviceTargets ?? []).flatMap((row) =>
              [
                ...(row.targetedDeviceTypes ?? []),
                ...(row.targetedOSes ?? []),
                ...(row.targetedBrands ?? []),
              ].map((value) => t(`targeting.device.values.${value}`, value.replace(/_/g, ' ')))
            )
          ),
        ])
      case 'keyword':
        return pipedChips(targeting.keywordTargets?.[0]?.keywords)
      case 'category':
        return pipedChips(targeting.categoryTargets?.[0]?.categories)
      case 'semantic':
        return (targeting.semanticTargets ?? []).map((row) =>
          chip(
            `${row.sentence.sentence} · ${semanticMatchLevel(row.score) || 1}/${SEMANTIC_MATCH_LEVELS.length}`,
            row.negated ? 'exclude' : 'include'
          )
        )
      case 'keyValue':
        return keyValueChips(targeting.keyValueTargets, t)
      case 'url':
        return textChips(targeting.articleTarget?.urls ?? [], listed)
      case 'domain':
        return textChips(targeting.domainNameTarget?.names ?? [], listed)
      case 'ipAddress':
        return textChips(targeting.ipAddressTarget?.addresses ?? [])
      case 'viewability':
        return [
          chip(t('targeting.atLeastPercent', { value: targeting.viewabilityTarget?.viewability })),
        ]
      case 'dayParting':
        return (targeting.dayPartingTargets ?? []).map((row) => {
          const days = (row.daysOfWeek ?? []).map((day) => t(`targeting.days.${day}`)).join(', ')
          const hours = [...(row.hoursOfDay ?? [])].sort((a, b) => a - b)
          const window = hours.length
            ? `${String(hours[0]).padStart(2, '0')}–${String(hours[hours.length - 1] + 1).padStart(2, '0')}`
            : ''
          return chip([days, window].filter(Boolean).join(' '))
        })
      case 'date':
        return (targeting.dateTarget?.dateRanges ?? []).map((range) =>
          chip(`${range.first.replace('T', ' ')} → ${range.second.replace('T', ' ')}`)
        )
    }
  }

  return TARGETING_TYPES.filter((def) => def.count(targeting) > 0).map((def) => {
    const negated = !!def.negated?.(targeting)
    return {
      id: def.id,
      label: t(`targeting.types.${def.id}.plural`),
      chips: ALWAYS_COLOURED.includes(def.id)
        ? chipsFor(def.id, negated)
        : tone(chipsFor(def.id, negated)),
      negated,
      count: def.count(targeting),
    }
  })
}
